"use strict";
/**
 * Patterns canonical definition — the single source of truth for the `patterns`
 * OpenSearch index family.
 *
 * Per the canonical-shared-types model, `shared-types` (the pure, AWS-SDK-free contract
 * package) holds, per index family, BOTH:
 *   (a) the field-name constants (`PATTERN_FIELDS`) — runtime values typed against
 *       `PatternSearchDocument` keys so the contract and the constants cannot drift, and
 *   (b) the OpenSearch template body (`buildPatternTemplate`) — settings (INCLUDING the
 *       `analysis` analyzers/normalizers), `dynamic: true`, and explicit `properties`.
 *
 * Every other surface (the reindex-script registrar, the indexer writer, the search
 * reader) references these instead of authoring its own copy, so the BF-020 difficulty
 * drift — writer `difficulty` vs reader/mapping `difficulty_level` — becomes structurally
 * impossible: there is exactly one string `'difficulty_level'`, exported once.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PATTERN_FIELDS = void 0;
exports.buildPatternTemplate = buildPatternTemplate;
/**
 * Canonical field-name constants for the `patterns` index family.
 *
 * Each value is a field name constrained to be a real key of `PatternSearchDocument` via
 * `satisfies Record<string, keyof PatternSearchDocument>`, so a typo or a rename that
 * breaks the contract fails to compile. `difficulty_level` is the CANONICAL difficulty
 * field — there is intentionally NO `difficulty` key (the deprecated `difficulty?` member
 * is removed from the contract in a later task).
 */
exports.PATTERN_FIELDS = {
    pattern_id: 'pattern_id',
    title: 'title',
    display_description: 'display_description',
    difficulty_level: 'difficulty_level',
    tags: 'tags',
    images: 'images',
    review_count: 'review_count',
    star_counts: 'star_counts',
    author_id: 'author_id',
    screen_name: 'screen_name',
    published_at: 'published_at',
};
/**
 * Build the composable OpenSearch index-template body for the `patterns` family.
 *
 * Ported VERBATIM from the reindex script's `defaultPatternsTemplate`, including the full
 * `settings.analysis` block (the `fpw_text` analyzer, the `english_*` filters, and the
 * `lowercase` normalizer) which `title`/`tags`/`screen_name`/`difficulty_level` reference.
 * `mappings.dynamic` is the literal `true` (the BF-018 guard). Property keys that are also
 * `PATTERN_FIELDS` field names are keyed by the constants so the mapping types exactly the
 * fields the constants name; mapping-only keys (`pattern_materials`, `pattern_description`,
 * `average_rating`, `favorites_count`, `author_reputation`, `published_date`, `is_public`,
 * `version`) are template-internal and kept as plain string keys.
 */
function buildPatternTemplate(opts) {
    return {
        index_patterns: [opts.pattern],
        template: {
            settings: {
                number_of_shards: opts.shards ?? 1,
                number_of_replicas: opts.replicas ?? 0,
                refresh_interval: opts.refresh ?? '5s',
                analysis: {
                    analyzer: {
                        fpw_text: {
                            type: 'custom',
                            tokenizer: 'standard',
                            filter: ['lowercase', 'asciifolding', 'english_possessive_stemmer', 'english_stop', 'english_stemmer'],
                        },
                    },
                    filter: {
                        english_stop: { type: 'stop', stopwords: '_english_' },
                        english_stemmer: { type: 'stemmer', language: 'english' },
                        english_possessive_stemmer: { type: 'stemmer', language: 'possessive_english' },
                    },
                    normalizer: {
                        lowercase: { type: 'custom', char_filter: [], filter: ['lowercase', 'asciifolding'] },
                    },
                },
            },
            mappings: {
                dynamic: true,
                properties: {
                    [exports.PATTERN_FIELDS.pattern_id]: { type: 'keyword' },
                    [exports.PATTERN_FIELDS.title]: {
                        type: 'text',
                        analyzer: 'fpw_text',
                        fields: {
                            raw: { type: 'keyword' },
                            sasy: { type: 'search_as_you_type' },
                        },
                    },
                    [exports.PATTERN_FIELDS.tags]: {
                        type: 'keyword',
                        normalizer: 'lowercase',
                        fields: { text: { type: 'text', analyzer: 'fpw_text' } },
                    },
                    pattern_materials: {
                        type: 'keyword',
                        normalizer: 'lowercase',
                        fields: { text: { type: 'text', analyzer: 'fpw_text' } },
                    },
                    [exports.PATTERN_FIELDS.display_description]: { type: 'text', analyzer: 'fpw_text' },
                    pattern_description: { type: 'text', index: false },
                    [exports.PATTERN_FIELDS.screen_name]: {
                        type: 'keyword',
                        normalizer: 'lowercase',
                        fields: { text: { type: 'text', analyzer: 'fpw_text' } },
                    },
                    [exports.PATTERN_FIELDS.difficulty_level]: { type: 'keyword', normalizer: 'lowercase' },
                    average_rating: { type: 'float' },
                    [exports.PATTERN_FIELDS.review_count]: { type: 'integer' },
                    [exports.PATTERN_FIELDS.star_counts]: {
                        type: 'object',
                        properties: {
                            1: { type: 'integer' },
                            2: { type: 'integer' },
                            3: { type: 'integer' },
                            4: { type: 'integer' },
                            5: { type: 'integer' },
                        },
                    },
                    favorites_count: { type: 'integer' },
                    author_reputation: { type: 'float' },
                    [exports.PATTERN_FIELDS.author_id]: { type: 'keyword' },
                    published_date: { type: 'date' },
                    [exports.PATTERN_FIELDS.images]: { type: 'keyword' },
                    is_public: { type: 'boolean' },
                    version: { type: 'integer' },
                },
            },
        },
        priority: 100,
        _meta: { managed_by: 'fpw', purpose: 'patterns' },
    };
}
